import { useState, useEffect } from 'react'
import {
  getUnansweredQuestions,
  ChatbotLog,
} from '@/services/chatbot-analytics'
import {
  createKnowledgeBaseEntry,
  KnowledgeBaseInsert,
} from '@/services/chatbot-config'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Sparkles } from 'lucide-react'
import { format } from 'date-fns'
import { KnowledgeBaseDialog } from './KnowledgeBaseDialog'

export const TrainingTab = () => {
  const [logs, setLogs] = useState<ChatbotLog[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLog, setSelectedLog] = useState<ChatbotLog | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const fetchLogs = async () => {
    setLoading(true)
    const { data, error } = await getUnansweredQuestions()
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load logs',
      })
    } else {
      setLogs(data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchLogs()
  }, [])

  const handleTrain = (log: ChatbotLog) => {
    setSelectedLog(log)
    setIsDialogOpen(true)
  }

  const handleCreateEntry = async (values: KnowledgeBaseInsert) => {
    setIsSubmitting(true)
    try {
      await createKnowledgeBaseEntry(values)
      toast({
        title: 'Knowledge added',
        description: 'The bot will now respond to this pattern.',
      })
      setIsDialogOpen(false)
      // Ideally we would update the log to is_unanswered=false here, but that's an extra step not strictly required by user story
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create entry',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) return <Loader2 className="h-8 w-8 animate-spin mx-auto mt-10" />

  return (
    <div className="space-y-4">
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User Message</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  No unanswered questions found. Good job!
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-medium">
                    {log.user_message}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {format(new Date(log.created_at), 'MMM dd, HH:mm')}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 border-primary text-primary hover:bg-primary/5"
                      onClick={() => handleTrain(log)}
                    >
                      <Sparkles className="h-4 w-4" /> Train
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <KnowledgeBaseDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        initialKeywords={selectedLog?.user_message || ''}
        onSubmit={handleCreateEntry}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
